/**
 * Sheet template definitions for the New Sheet dialog.
 *
 * Each template pairs a paper size (title block id) with a pre-printed
 * "letterhead" style (antet) rendered by SheetView: Standard (plain border),
 * Corporate (brand band + logo zone), Minimal (thin frame), and Engineering
 * (grid ruler edges + border zones).
 */

export type SheetStyleId = 'standard' | 'corporate' | 'minimal' | 'engineering';

export interface SheetTemplate {
    /** Title block id passed to the kernel (paper type). */
    titleBlockId: string;
    name: string;
    /** Display size, e.g. "841 x 594 mm". */
    sizeLabel: string;
    widthMm: number;
    heightMm: number;
    style: SheetStyleId;
    /** Short description shown under the template card. */
    description: string;
}

export const SHEET_TEMPLATES: SheetTemplate[] = [
    {
        titleBlockId: 'tb-a1',
        name: 'A1 Antetli (Standard)',
        sizeLabel: '841 x 594 mm',
        widthMm: 841,
        heightMm: 594,
        style: 'standard',
        description: 'Classic title block strip on the right, A1 landscape.',
    },
    {
        titleBlockId: 'tb-a2',
        name: 'A2 Antetli (Standard)',
        sizeLabel: '594 x 420 mm',
        widthMm: 594,
        heightMm: 420,
        style: 'standard',
        description: 'Compact A2 landscape with standard title block.',
    },
    {
        titleBlockId: 'tb-a3',
        name: 'A3 Antetli (Standard)',
        sizeLabel: '420 x 297 mm',
        widthMm: 420,
        heightMm: 297,
        style: 'standard',
        description: 'A3 landscape for detail sheets.',
    },
    {
        titleBlockId: 'tb-a0',
        name: 'A0 Kurumsal (Corporate)',
        sizeLabel: '1189 x 841 mm',
        widthMm: 1189,
        heightMm: 841,
        style: 'corporate',
        description: 'Large format with corporate band and logo zone.',
    },
    {
        titleBlockId: 'tb-24x36',
        name: 'Arch D Kurumsal (Corporate)',
        sizeLabel: '914 x 610 mm',
        widthMm: 914.4,
        heightMm: 609.6,
        style: 'corporate',
        description: 'Architectural D-size sheet with corporate letterhead.',
    },
    {
        titleBlockId: 'tb-a4',
        name: 'A4 Minimal',
        sizeLabel: '297 x 210 mm',
        widthMm: 297,
        heightMm: 210,
        style: 'minimal',
        description: 'Thin border, discreet corner block. Small-format reports.',
    },
    {
        titleBlockId: 'tb-ansi-b',
        name: 'ANSI B Minimal',
        sizeLabel: '432 x 279 mm',
        widthMm: 431.8,
        heightMm: 279.4,
        style: 'minimal',
        description: '11 x 17 tabloid sheet with minimal frame.',
    },
    {
        titleBlockId: 'tb-30x42',
        name: 'Arch E1 Mühendislik (Engineering)',
        sizeLabel: '1067 x 762 mm',
        widthMm: 1066.8,
        heightMm: 762,
        style: 'engineering',
        description: 'Grid ruler edges, zone references and full title block.',
    },
    {
        titleBlockId: 'tb-ansi-e',
        name: 'ANSI E Mühendislik (Engineering)',
        sizeLabel: '864 x 1118 mm',
        widthMm: 863.6,
        heightMm: 1117.6,
        style: 'engineering',
        description: 'ANSI E portrait engineering sheet with zone grid.',
    },
];

export const SHEET_STYLE_LABELS: Record<SheetStyleId, string> = {
    standard: 'Standard',
    corporate: 'Kurumsal',
    minimal: 'Minimal',
    engineering: 'Mühendislik',
};
